var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { CONFIG } from "@/config";
import { getPageIndex, getPageNumber, SheetSide } from "@/utils/pages";
import { Canvas } from "@react-three/fiber";
import classNames from "classnames";
import { useAtom } from "jotai";
import { useAtomValue, useUpdateAtom } from "jotai/utils";
import _, { cloneDeep } from "lodash";
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { Vector3 } from "three";
import { CameraIntroComplete, CurrentSheetAtom, EffectiveSfxVolumeAtom, MouseOverClickablePage, OverElement, PageOverElement, PagesAtom, } from "../atoms";
import { BackgroundFXPlayer } from "../audio/BackgroundFXPlayer";
import { PageFXPlayerAtom } from "../audio/PageFXPlayer";
import { getAudioForPages } from "../meta/ArtbookAudio";
import styles from "./Artbook.module.scss";
import { CameraControl } from "./CameraControl";
import { PageLoader, PageSide } from "./Page";
import { Summary } from "./Summary";
import { Table } from "./Table";
import { metrics } from "../analytics/events";
import { FPSMeter, FPSMonitor } from "../utils/fps";
import { useDisposable } from "../utils/useDisposable";
import { UrlLocale } from "../ubisoft/locale";
import { DEFAULT_LOCALE } from "../locales";
import { OrbitControls } from "@react-three/drei";
export function Artbook(_a) {
    var _b;
    var hidden = _a.hidden;
    var isOver = useAtomValue(OverElement);
    var isPageOver = useAtomValue(MouseOverClickablePage);
    var _c = __read(useState(2), 2), dpr = _c[0], setDpr = _c[1];
    var fpsMeter = useMemo(function () { return new FPSMeter(10); }, []);
    var fpsMonitor = useMemo(function () { return new FPSMonitor(fpsMeter, 25, 5000); }, [fpsMeter]);
    useEffect(function () {
        fpsMonitor.onUnderThreshold = function () {
            setDpr(1);
        };
        fpsMeter.start();
        fpsMonitor.start();
        return function () {
            fpsMeter.stop();
            fpsMonitor.stop();
        };
    }, [fpsMeter, fpsMonitor]);
    return (React.createElement("div", { className: classNames(styles.artbook, (_b = {}, _b[styles.hidden] = hidden, _b)), style: { cursor: isOver || isPageOver ? "pointer" : "" } },
        React.createElement(Canvas, { dpr: dpr, flat: true },
            React.createElement(ArtbookContent, null))));
}
export function ArtbookContent() {
    var pages = useAtomValue(PagesAtom);
    var locale = useAtomValue(UrlLocale) || DEFAULT_LOCALE;
    var _a = __read(useState(true), 2), closed = _a[0], setClosed = _a[1];
    var onProgress = useCallback(function (p) {
        // TODO: refacto timing config
        var triggerProgress = CONFIG.skipIntroAnimation ? 0.8 : 0.8;
        if (closed && p > triggerProgress) {
            setClosed(false);
        }
    }, [closed]);
    var _b = __read(useAtom(CurrentSheetAtom), 2), targetLeftSheet = _b[0], _setLeftSheet = _b[1];
    var timeout = useRef({ lastUpdate: Date.now() });
    var setLeftSheet = useCallback(function (value) {
        if (value === "prev" || value === "next") {
            if (Date.now() - timeout.current.lastUpdate < 400) {
                return;
            }
        }
        timeout.current.lastUpdate = Date.now();
        _setLeftSheet(value);
    }, [_setLeftSheet]);
    var leftSheet = closed ? -1 : targetLeftSheet;
    var _c = __read(useState(0), 2), bookAnimating = _c[0], setBookAnimating = _c[1];
    var sfxVolume = useAtomValue(EffectiveSfxVolumeAtom);
    var pageFxPlayer = useAtomValue(PageFXPlayerAtom);
    var fxPlayer = useDisposable(function () {
        var fxPlayer = new BackgroundFXPlayer();
        return [
            fxPlayer,
            function () {
                fxPlayer.dispose();
            },
        ];
    }, []);
    useEffect(function () {
        function onKeyPress(e) {
            if (e.code === "ArrowLeft") {
                setLeftSheet("prev");
                metrics.pressOnLeftPageKeyboardShortcut();
            }
            if (e.code === "ArrowRight") {
                setLeftSheet("next");
                metrics.pressOnRightPageKeyboardShortcut();
            }
        }
        document.addEventListener("keydown", onKeyPress);
        return function () {
            document.removeEventListener("keydown", onKeyPress);
        };
    }, [setLeftSheet]);
    useEffect(function () {
        fxPlayer === null || fxPlayer === void 0 ? void 0 : fxPlayer.setVolume(sfxVolume);
        pageFxPlayer.setVolume(sfxVolume);
    }, [fxPlayer, pageFxPlayer, sfxVolume]);
    var _d = __read(useState(leftSheet), 2), visibleLeftSheet = _d[0], setVisibleLeftSheet = _d[1];
    useEffect(function () {
        var leftPage = getPageNumber(visibleLeftSheet, SheetSide.back);
        if (bookAnimating > 0) {
            fxPlayer === null || fxPlayer === void 0 ? void 0 : fxPlayer.setTrack(null);
        }
        else {
            fxPlayer === null || fxPlayer === void 0 ? void 0 : fxPlayer.setTrack(getAudioForPages(leftPage, locale));
        }
    }, [fxPlayer, visibleLeftSheet, bookAnimating, locale]);
    useEffect(function () {
        if (leftSheet === visibleLeftSheet)
            return;
        var timeout = setTimeout(function () {
            if (leftSheet !== visibleLeftSheet) {
                setVisibleLeftSheet(leftSheet);
            }
        }, 1);
        return function () {
            clearTimeout(timeout);
        };
    }, [leftSheet, visibleLeftSheet]);
    var pageGroup = useRef(null);
    var alignBookControlsEnabled = CONFIG.models.artbook.transform.debug;
    var alignBookCameraEnabled = CONFIG.models.artbook.transform.debugCamera;
    var _e = __read(useState({
        scale: CONFIG.models.artbook.transform.scale,
        position: new Vector3(CONFIG.models.artbook.transform.position[0], CONFIG.models.artbook.transform.position[1], CONFIG.models.artbook.transform.position[2]),
    }), 2), transform = _e[0], _setTransform = _e[1];
    useEffect(function () {
        if (!alignBookControlsEnabled)
            return;
        window.DEBUG_DT = 0.005;
        window.DEBUG_DT_SCALE = 0.005;
        console.log("change DEBUG_DT var to change movement speed");
        function setTransform(update) {
            _setTransform(function (e) {
                update(e);
                console.log(JSON.stringify({
                    scale: e.scale,
                    position: [e.position.x, e.position.y, e.position.z],
                }));
                return cloneDeep(e);
            });
        }
        function listener(e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            var DT = window.DEBUG_DT || 0.005;
            var DT_SCALE = window.DEBUG_DT_SCALE || 0.005;
            if (e.code === "NumpadAdd") {
                setTransform(function (transform) {
                    transform.scale += DT_SCALE;
                });
            }
            if (e.code === "NumpadSubtract") {
                setTransform(function (transform) {
                    transform.scale -= DT_SCALE;
                });
            }
            if (e.code === "Numpad8") {
                setTransform(function (transform) {
                    transform.position.z -= DT;
                });
            }
            if (e.code === "Numpad2") {
                setTransform(function (transform) {
                    transform.position.z += DT;
                });
            }
            if (e.code === "Numpad4") {
                setTransform(function (transform) {
                    transform.position.x -= DT;
                });
            }
            if (e.code === "Numpad6") {
                setTransform(function (transform) {
                    transform.position.x += DT;
                });
            }
            if (e.code === "Numpad9") {
                setTransform(function (transform) {
                    transform.position.y += DT;
                });
            }
            if (e.code === "Numpad3") {
                setTransform(function (transform) {
                    transform.position.y -= DT;
                });
            }
        }
        document.addEventListener("keypress", listener);
    }, [alignBookControlsEnabled]);
    return (React.createElement("group", null,
        alignBookCameraEnabled ? (React.createElement(OrbitControls, null)) : (React.createElement(CameraControl, { perspective: true })),
        React.createElement("group", { position: [0.15, -0.45, 0] },
            React.createElement(Table, { scale: transform.scale, position: transform.position, onProgress: onProgress, onRightPagePos: function (pos) {
                    if (pageGroup.current != null && pageGroup.current.parent != null) {
                        pageGroup.current.position.x =
                            pageGroup.current.parent.worldToLocal(new Vector3(pos, 0, 0))
                                .x + 0.15;
                    }
                }, onClickBookmark: function () { return setLeftSheet(0); } }),
            React.createElement("group", { ref: pageGroup },
                React.createElement(BookCanvas, { pages: pages, leftSheet: visibleLeftSheet, onNext: function () { return setLeftSheet("next"); }, onPrev: function () { return setLeftSheet("prev"); }, onAnimatingState: function (pages) {
                        setBookAnimating(pages);
                    } }),
                leftSheet === 0 && !bookAnimating ? (React.createElement(Suspense, { fallback: null },
                    React.createElement(Summary, { goToPage: function (page) {
                            setLeftSheet(Math.floor(page / 2));
                            metrics.clickOnSummaryShortcut(Math.floor(page / 2));
                        } }))) : null))));
}
function eqSheet(a, b) {
    return a.from === b.from && a.to === b.to;
}
function BookCanvas(_a) {
    var pages = _a.pages, leftSheet = _a.leftSheet, onPrev = _a.onPrev, onNext = _a.onNext, onAnimatingState = _a.onAnimatingState;
    var _b = __read(useState({
        type: "static",
        leftSheet: leftSheet,
    }), 2), state = _b[0], setBookState = _b[1];
    // After 15s of inactivity on the first page, play an animation to invite to interact
    var _c = __read(useState(false), 2), pageFolded = _c[0], setPageFolded = _c[1];
    var foldTimeout = useRef(-1);
    useEffect(function () {
        setPageFolded(false);
        if (state.type === "static" && leftSheet === 0) {
            foldTimeout.current = setTimeout(function () {
                setPageFolded(true);
            }, 15000);
        }
        else if (foldTimeout.current !== -1) {
            clearTimeout(foldTimeout.current);
        }
    }, [state, leftSheet]);
    useEffect(function () {
        if (state.type === "static") {
            metrics.artbookPageDisplayed(state.leftSheet);
            if (state.leftSheet === 0) {
                metrics.artbookSummaryDisplayed();
            }
        }
    }, [state]);
    var movingSheets = state.type === "animating" ? state.movingSheets.length : 0;
    useEffect(function () {
        onAnimatingState(movingSheets);
    }, [onAnimatingState, movingSheets]);
    var currentLeftSheet = getCurrentLeftSheet(state);
    var currentRightSheet = getCurrentRightSheet(state);
    var rightSheet = leftSheet + 1;
    function _addSheets(sheetsToAdd) {
        setBookState(function (state) {
            // console.log("[add]", sheetsToAdd);
            if (state.type === "static") {
                return {
                    type: "animating",
                    movingSheets: sheetsToAdd,
                };
            }
            else {
                return {
                    type: "animating",
                    movingSheets: _.uniqBy(__spreadArray(__spreadArray([], __read(state.movingSheets), false), __read(sheetsToAdd), false), function (sheet) { return "".concat(sheet.from, ":").concat(sheet.to); }),
                };
            }
        });
    }
    var PAGE_DT = 400;
    useEffect(function () {
        if (leftSheet < currentLeftSheet) {
            if (currentLeftSheet === leftSheet + 1) {
                _addSheets([
                    {
                        from: currentLeftSheet,
                        to: currentLeftSheet,
                        animationStartTime: Date.now(),
                    },
                ]);
            }
            else if (currentLeftSheet === leftSheet + 2) {
                _addSheets([
                    {
                        from: leftSheet + 1,
                        to: currentLeftSheet - 1,
                        animationStartTime: Date.now() + PAGE_DT,
                    },
                    {
                        from: currentLeftSheet,
                        to: currentLeftSheet,
                        animationStartTime: Date.now(),
                    },
                ]);
            }
            else {
                _addSheets([
                    {
                        from: leftSheet + 1,
                        to: leftSheet + 1,
                        animationStartTime: Date.now() + PAGE_DT * 2.5,
                    },
                    {
                        from: leftSheet + 2,
                        to: currentLeftSheet - 1,
                        animationStartTime: Date.now() + PAGE_DT,
                    },
                    {
                        from: currentLeftSheet,
                        to: currentLeftSheet,
                        animationStartTime: Date.now(),
                    },
                ]);
            }
            // const sheetsToAdd = _.range(leftSheet + 1, currentLeftSheet + 1).map(
            //   (id) => ({ from: id, to: id })
            // );
            // _addSheets(sheetsToAdd);
        }
        if (rightSheet > currentRightSheet) {
            if (currentRightSheet === rightSheet - 1) {
                _addSheets([
                    {
                        from: currentRightSheet,
                        to: currentRightSheet,
                        animationStartTime: Date.now(),
                    },
                ]);
            }
            else if (currentRightSheet === rightSheet - 2) {
                _addSheets([
                    {
                        from: currentRightSheet,
                        to: currentRightSheet,
                        animationStartTime: Date.now(),
                    },
                    {
                        from: currentRightSheet + 1,
                        to: rightSheet - 1,
                        animationStartTime: Date.now() + PAGE_DT,
                    },
                ]);
            }
            else {
                _addSheets([
                    {
                        from: currentRightSheet,
                        to: currentRightSheet,
                        animationStartTime: Date.now(),
                    },
                    {
                        from: currentRightSheet + 1,
                        to: rightSheet - 2,
                        animationStartTime: Date.now() + PAGE_DT,
                    },
                    {
                        from: rightSheet - 1,
                        to: rightSheet - 1,
                        animationStartTime: Date.now() + PAGE_DT * 2.5,
                    },
                ]);
            }
            // const sheetsToAdd = _.range(currentRightSheet, rightSheet).map((id) => ({
            //   from: id,
            //   to: id,
            // }));
            // _addSheets(sheetsToAdd);
        }
    }, [currentLeftSheet, currentRightSheet, leftSheet, rightSheet]);
    function removeAnimatedSheet(sheet) {
        setBookState(function (state) {
            // console.log("[remove]", sheet);
            if (state.type === "static")
                return state;
            var movingSheets = state.movingSheets.filter(function (e) { return !eqSheet(e, sheet); });
            if (movingSheets.length === 0) {
                if (sheet.to > leftSheet && sheet.from < leftSheet) {
                    return {
                        type: "animating",
                        movingSheets: [
                            {
                                from: sheet.from,
                                to: leftSheet,
                                animationStartTime: Date.now(),
                            },
                        ],
                    };
                }
                else {
                    return {
                        type: "static",
                        leftSheet: leftSheet,
                    };
                }
            }
            else {
                return {
                    type: "animating",
                    movingSheets: movingSheets,
                };
            }
        });
    }
    var displayedSheets = useMemo(function () { return getDisplayedSheets(state, pageFolded); }, [state, pageFolded]);
    var pageTurnArea = CONFIG.pageTurnArea, pagesConfig = CONFIG.pages;
    var setOverElement = useUpdateAtom(PageOverElement);
    var introComplete = useAtomValue(CameraIntroComplete);
    useEffect(function () {
        return function () {
            setOverElement({ action: "reset" });
        };
    }, [setOverElement]);
    return (React.createElement("group", { rotation: [0, 0, 0], position: [-0.15, 0.45, -0.01] },
        React.createElement("mesh", { name: "pageTurnRight", position: [
                pagesConfig.offset.x + pageTurnArea.offset,
                0,
                pagesConfig.offset.y,
            ], scale: [pageTurnArea.width, 0.2, pageTurnArea.height], onPointerOver: function () {
                setOverElement({ action: "over", element: "right" });
            }, onPointerOut: function () {
                setOverElement({ action: "out", element: "right" });
            }, onClick: function () {
                if (introComplete) {
                    onNext();
                    metrics.clickOnRightPage();
                }
            } },
            React.createElement("meshBasicMaterial", { visible: pageTurnArea.debug }),
            React.createElement("boxGeometry", null)),
        React.createElement("mesh", { name: "pageTurnLeft", position: [
                pagesConfig.offset.x - pageTurnArea.offset,
                0,
                pagesConfig.offset.y,
            ], scale: [pageTurnArea.width, 0.2, pageTurnArea.height], onPointerOver: function () {
                setOverElement({ action: "over", element: "left" });
            }, onPointerOut: function () {
                setOverElement({ action: "out", element: "left" });
            }, onClick: function () {
                if (introComplete) {
                    onPrev();
                    metrics.clickOnLeftPage();
                }
            } },
            React.createElement("meshBasicMaterial", { visible: pageTurnArea.debug }),
            React.createElement("boxGeometry", null)),
        displayedSheets.map(function (sheet) {
            if (sheet.to < 0 || sheet.from > pages.getSheetCount())
                return null;
            var side = sheet.to <= leftSheet ? PageSide.Left : PageSide.Right;
            return (React.createElement(Suspense, { fallback: null, key: "page:".concat(sheet.from, ":").concat(sheet.to) },
                React.createElement(PageLoader, { revealedSides: sheet.revealedSides, animationStartTime: sheet.animationStartTime, inFront: sheet.hd, pages: pages, pageFront: getPageIndex(sheet.from, SheetSide.front), pageBack: getPageIndex(sheet.to, SheetSide.back), animating: sheet.animating, side: side, depth: sheet.depth, 
                    // onTransitionStart={() => addAnimatedSheet(sheetIndex)}
                    onTransitionEnd: function () { return removeAnimatedSheet(sheet); }, fold: sheet.fold })));
        })));
}
function getCurrentLeftSheet(state) {
    return state.type === "static"
        ? state.leftSheet
        : _.chain(state.movingSheets)
            .map(function (s) { return s.from; })
            .min()
            .value() - 1;
}
function getCurrentRightSheet(state) {
    return state.type === "static"
        ? state.leftSheet + 1
        : _.chain(state.movingSheets)
            .map(function (s) { return s.to; })
            .max()
            .value() + 1;
}
function getDisplayedSheets(state, fold) {
    if (state.type === "static") {
        return [
            {
                from: state.leftSheet - 1,
                to: state.leftSheet - 1,
                animating: false,
                animationStartTime: 0,
                depth: 1,
                hd: false,
                revealedSides: [],
                fold: false,
            },
            {
                from: state.leftSheet,
                to: state.leftSheet,
                animating: false,
                animationStartTime: 0,
                depth: 0,
                hd: true,
                revealedSides: [PageSide.Left],
                fold: false,
            },
            {
                from: state.leftSheet + 1,
                to: state.leftSheet + 1,
                animating: false,
                animationStartTime: 0,
                depth: 0,
                hd: true,
                revealedSides: [PageSide.Right],
                fold: fold,
            },
            {
                from: state.leftSheet + 2,
                to: state.leftSheet + 2,
                animating: false,
                animationStartTime: 0,
                depth: 1,
                hd: false,
                revealedSides: [],
                fold: false,
            },
        ];
    }
    var leftSheet = getCurrentLeftSheet(state);
    var rightSheet = getCurrentRightSheet(state);
    return __spreadArray(__spreadArray([
        {
            from: leftSheet - 1,
            to: leftSheet - 1,
            animating: false,
            animationStartTime: 0,
            depth: 1,
            hd: false,
            revealedSides: [],
            fold: false,
        },
        {
            from: leftSheet,
            to: leftSheet,
            animating: false,
            animationStartTime: 0,
            depth: 0,
            hd: false,
            revealedSides: [PageSide.Left],
            fold: false,
        }
    ], __read(state.movingSheets.map(function (s) { return (__assign(__assign({}, s), { animating: true, animationStartTime: s.animationStartTime, depth: -1, hd: false, revealedSides: [PageSide.Left, PageSide.Right], fold: false })); })), false), [
        {
            from: rightSheet,
            to: rightSheet,
            animating: false,
            animationStartTime: 0,
            depth: 0,
            hd: false,
            revealedSides: [PageSide.Right],
            fold: false,
        },
        {
            from: rightSheet + 1,
            to: rightSheet + 1,
            animating: false,
            animationStartTime: 0,
            depth: 1,
            hd: false,
            revealedSides: [],
            fold: false,
        },
    ], false);
}
